<template>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- Total Applications -->
        <div class="bg-white p-6 shadow-md rounded-lg border-l-4 border-blue-500">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">Total Applications</p>
                    <p class="text-3xl font-bold text-gray-900">{{ getFormatNumberInternational(getDataValue('summary',
                        'total_applications')) }}</p>
                </div>
                <div class="p-3 bg-blue-100 rounded-full">
                    <font-awesome-icon :icon="['fas', 'file-alt']" class="text-blue-600 text-xl" />
                </div>
            </div>
        </div>

        <!-- Accepted Applications -->
        <div class="bg-white p-6 shadow-md rounded-lg border-l-4 border-green-500">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">Accepted</p>
                    <p class="text-3xl font-bold text-green-600">{{ getFormatNumberInternational(getDataValue('summary',
                        'accepted_applications')) }}</p>
                    <p class="text-xs text-gray-500">
                        {{ getPercentage(getDataValue('summary', 'accepted_applications'), getDataValue('summary',
                            'total_applications')) }}% of total
                    </p>
                </div>
                <div class="p-3 bg-green-100 rounded-full">
                    <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-600 text-xl" />
                </div>
            </div>
        </div>

        <!-- Rejected Applications -->
        <div class="bg-white p-6 shadow-md rounded-lg border-l-4 border-red-500">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">Rejected</p>
                    <p class="text-3xl font-bold text-red-600">{{ getFormatNumberInternational(getDataValue('summary',
                        'rejected_applications')) }}</p>
                    <p class="text-xs text-gray-500">
                        {{ getPercentage(getDataValue('summary', 'rejected_applications'), getDataValue('summary',
                            'total_applications')) }}% of total
                    </p>
                </div>
                <div class="p-3 bg-red-100 rounded-full">
                    <font-awesome-icon :icon="['fas', 'times-circle']" class="text-red-600 text-xl" />
                </div>
            </div>
        </div>

        <!-- Acceptance Rate -->
        <div class="bg-white p-6 shadow-md rounded-lg border-l-4 border-purple-500">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">Acceptance Rate</p>
                    <p class="text-3xl font-bold text-purple-600">{{ getAcceptanceRate() }}%</p>
                    <p class="text-xs text-gray-500">Success rate</p>
                </div>
                <div class="p-3 bg-purple-100 rounded-full">
                    <font-awesome-icon :icon="['fas', 'chart-line']" class="text-purple-600 text-xl" />
                </div>
            </div>
        </div>
    </div>

    <!-- Gender and Local/Non-Local Breakdown -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        <!-- Male Applications -->
        <div class="bg-white p-6 shadow-md rounded-lg">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600 flex items-center">
                        <font-awesome-icon :icon="['fas', 'male']" class="text-blue-600 mr-2" />
                        Male Applications
                    </p>
                    <p class="text-2xl font-bold text-blue-700">{{ getFormatNumberInternational(getDataValue('summary',
                        'male_applications')) }}</p>
                </div>
            </div>
        </div>

        <!-- Female Applications -->
        <div class="bg-white p-6 shadow-md rounded-lg">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600 flex items-center">
                        <font-awesome-icon :icon="['fas', 'female']" class="text-pink-500 mr-2" />
                        Female Applications
                    </p>
                    <p class="text-2xl font-bold text-pink-600">{{ getFormatNumberInternational(getDataValue('summary',
                        'female_applications')) }}</p>
                </div>
            </div>
        </div>

        <!-- Local Applications -->
        <div class="bg-white p-6 shadow-md rounded-lg">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600 flex items-center">
                        <font-awesome-icon :icon="['fas', 'home']" class="text-green-600 mr-2" />
                        Local Applications
                    </p>
                    <p class="text-2xl font-bold text-green-700">{{ getFormatNumberInternational(getDataValue('summary',
                        'local_applications')) }}</p>
                </div>
            </div>
        </div>

        <!-- Non-Local Applications -->
        <div class="bg-white p-6 shadow-md rounded-lg">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600 flex items-center">
                        <font-awesome-icon :icon="['fas', 'globe']" class="text-orange-600 mr-2" />
                        Non-Local Applications
                    </p>
                    <p class="text-2xl font-bold text-orange-700">{{
                        getFormatNumberInternational(getDataValue('summary', 'non_local_applications')) }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatNumberInternational } from "../../../helpers/utils";

export default {
    name: "AdmissionDetailsSummary",
    props: {
        data: {
            type: Object,
            default: () => ({})
        }
    },
    methods: {
        getFormatNumberInternational(number) {
            return formatNumberInternational(parseInt(number));
        },
        getDataValue(section, field) {
            if (this.data && this.data[section] && this.data[section][field] !== undefined) {
                const value = this.data[section][field];
                // Convert null or undefined values to 0
                return value === null || value === undefined ? 0 : value;
            }
            return 0;
        },
        getPercentage(part, total) {
            if (!total || total === 0) return 0;
            return ((part / total) * 100).toFixed(1);
        },
        getAcceptanceRate() {
            const accepted = this.getDataValue('summary', 'accepted_applications');
            const total = this.getDataValue('summary', 'total_applications');
            return this.getPercentage(accepted, total);
        }
    }
};
</script>